import { useState, type ReactNode } from 'react'
import { ChevronRight, Menu } from 'lucide-react'
import { NavLink, useLocation } from 'react-router-dom'

import { cn } from '@/custolink-newdesign/lib/utils'
import ShadcnScope from '@/custolink-newdesign/components/ShadcnScope'
import { Button } from '@/custolink-newdesign/ui/button'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/custolink-newdesign/ui/sheet'
import { TooltipProvider } from '@/custolink-newdesign/ui/tooltip'
import { navIcon } from '@/custolink-newdesign/shell/navIcons'
import navigationConfig from '@/configs/navigation.config'
import type { NavigationTree } from '@/@types/navigation'

/**
 * The settings sub-shell. flow SettingsShell.tsx:30-195.
 *
 * Introduces a secondary rail INSIDE the page, which the existing settings
 * pages do not have - today each one hand-rolls a breadcrumb of <h4> elements
 * (UserManagement.tsx:58-60, LookAndFeel.tsx:70, AssetFrontendSettings.tsx:19-24)
 * and they have already drifted apart. One shell means they cannot.
 *
 * The rail is driven by the Settings group in configs/navigation.config.ts, so
 * adding a settings route to that config puts it here automatically.
 *
 * Note the active idiom differs from the main rail on purpose: a tinted row
 * (bg-accent) rather than the raised card the main rail uses. flow does the
 * same - the main rail sits on --background where a card reads as raised, this
 * one sits beside a card where it would not.
 */

/** The Settings group in configs/navigation.config.ts. Historical key name. */
export const SETTINGS_GROUP_KEY = 'pricing-collapseMenu'

export function settingsItems(): NavigationTree[] {
  const group = navigationConfig.find((n) => n.key === SETTINGS_GROUP_KEY)
  return group?.subMenu ?? []
}

function NavTree({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="cl:flex cl:flex-col cl:gap-6 cl:p-3">
      <div className="cl:flex cl:flex-col cl:gap-1">
        <div className="cl:text-muted-foreground cl:px-3 cl:pb-1 cl:text-xs cl:font-semibold cl:tracking-wide cl:uppercase">
          Settings
        </div>
        {settingsItems().map((item) => {
          const Icon = navIcon(item.icon)
          return (
            <NavLink
              key={item.key}
              to={item.path}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'cl:flex cl:items-center cl:gap-3 cl:rounded-md cl:px-3 cl:py-2 cl:text-sm cl:font-medium cl:transition-colors',
                  'cl:focus-visible:ring-ring/50 cl:outline-none cl:focus-visible:ring-[3px]',
                  isActive
                    ? 'cl:bg-accent cl:text-accent-foreground'
                    : 'cl:text-foreground cl:hover:bg-muted'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={cn(
                      'cl:size-4 cl:shrink-0',
                      isActive ? 'cl:text-accent-foreground' : 'cl:text-muted-foreground'
                    )}
                  />
                  <span className="cl:truncate">{item.title}</span>
                </>
              )}
            </NavLink>
          )
        })}
      </div>
    </nav>
  )
}

/** Settings > [section]. Derived from the nav tree, not the URL. */
function SettingsBreadcrumb() {
  const { pathname } = useLocation()
  const current = settingsItems().find((i) => pathname.startsWith(i.path))

  return (
    <div className="cl:text-muted-foreground cl:flex cl:items-center cl:gap-1.5 cl:text-sm">
      <span>Settings</span>
      {current ? (
        <>
          <ChevronRight className="cl:size-3.5 cl:shrink-0" />
          <span className="cl:text-foreground cl:truncate cl:font-medium">{current.title}</span>
        </>
      ) : null}
    </div>
  )
}

export default function SettingsShell({
  title,
  description,
  actions,
  breadcrumbTail,
  wide = false,
  children,
}: {
  title: string
  description?: string
  /** Right-aligned controls on the title row. */
  actions?: ReactNode
  /** Appended after Settings > [section], for a detail page beneath a section. */
  breadcrumbTail?: ReactNode
  /** Lifts the 4xl cap for pages carrying tables rather than forms. */
  wide?: boolean
  children: ReactNode
}) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  return (
    <TooltipProvider>
      <ShadcnScope className="cl:bg-background cl:flex cl:min-h-0 cl:flex-1">
        <aside className="cl:hidden cl:w-72 cl:shrink-0 cl:overflow-y-auto cl:border-r cl:lg:block">
          <div className="cl:px-3 cl:pt-5 cl:pb-1 cl:text-2xl cl:font-semibold cl:tracking-tight">
            Settings
          </div>
          <NavTree />
        </aside>

        {/* The content column literally rounds into the rail. */}
        <div className="cl:bg-card cl:flex cl:min-h-0 cl:min-w-0 cl:flex-1 cl:flex-col cl:overflow-y-auto cl:lg:rounded-tl-xl">
          <div
            className={cn(
              'cl:flex cl:w-full cl:flex-col cl:gap-6 cl:p-4 cl:md:p-8',
              wide ? 'cl:max-w-6xl' : 'cl:max-w-4xl'
            )}
          >
            <div className="cl:flex cl:flex-col cl:gap-3">
              <div className="cl:flex cl:items-center cl:gap-3">
                <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="icon-sm" className="cl:lg:hidden">
                      <Menu />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="cl:w-80 cl:p-0">
                    <SheetTitle className="cl:px-6 cl:pt-5 cl:text-2xl cl:font-semibold cl:tracking-tight">
                      Settings
                    </SheetTitle>
                    <NavTree onNavigate={() => setMobileNavOpen(false)} />
                  </SheetContent>
                </Sheet>
                <SettingsBreadcrumb />
                {breadcrumbTail ? (
                  <>
                    <ChevronRight className="cl:text-muted-foreground cl:size-3.5 cl:shrink-0" />
                    <span className="cl:truncate cl:text-sm cl:font-medium">{breadcrumbTail}</span>
                  </>
                ) : null}
              </div>

              <div className="cl:flex cl:flex-wrap cl:items-start cl:justify-between cl:gap-3">
                <div className="cl:flex cl:min-w-0 cl:flex-col cl:gap-1">
                  <h1 className="cl:text-3xl cl:font-semibold cl:tracking-tight">{title}</h1>
                  {description ? (
                    <p className="cl:text-muted-foreground cl:text-sm">{description}</p>
                  ) : null}
                </div>
                {actions ? (
                  <div className="cl:flex cl:shrink-0 cl:items-center cl:gap-2">{actions}</div>
                ) : null}
              </div>
            </div>

            {children}
          </div>
        </div>
      </ShadcnScope>
    </TooltipProvider>
  )
}
